import React, { useEffect, useMemo, useState } from "react";
import moment from "moment";
import { useDispatch } from "react-redux";
import Teachr from "../../Objects/models/Teachr";
import Client from "../../Objects/models/Client";
import SupportRepository from "../../Repositories/SupportRepository";
import PunctualCourseRepository, { TeachrCourseSessionInfo } from "../../Repositories/PunctualCourseRepository";
import ClientSessionRepository, { ClientSessionInfo } from "../../Repositories/ClientSessionRepository";
import { capitalizeFirstLetter } from "../../Utils/Function";
import { SHOW_ERROR, SHOW_SUCCESS } from "../../Redux/Reducers/MessageReducers";
import { SUPPORT_SUBJECTS } from "../../config/supportContact";
import { DV2 } from "../DeclarationV2/theme";
import { clock, dayLabel } from "../DeclarationV2/format";
import { vocab } from "../DeclarationV2/vocabulary";
import { Button, Card, FieldError, FieldLabel, Hint, fieldStyle, useDv2Theme } from "../DeclarationV2/layout/Ui";

const MAX_MESSAGE_LENGTH = 2000;
const MIN_MESSAGE_LENGTH = 10;
const CALLBACK_SLOTS = ["En matinée", "En début d'après-midi", "En fin de journée"];

interface Props {
    /** Prof connecté (page Aide du prof). */
    teachr?: Teachr;
    /** Client connecté (page Aide du client) : les cours proposés sont les siens. */
    client?: Client;
    /** Sujet présélectionné (doit figurer dans SUPPORT_SUBJECTS). */
    defaultSubject?: string;
    /** Case « Je préfère être rappelé » cochée à l'ouverture (bouton « Être rappelé » du démarrage). */
    defaultCallback?: boolean;
    /** Contexte transmis à l'équipe avec le message (fiche d'un cours côté client : « Cours n°123 du … »). */
    defaultContext?: string;
}

/** Option de la liste « cours concerné ». */
interface CourseOption { key: string; label: string; context: string }

/**
 * Formulaire « Aide et contact » (maquettes 1l et 2l) : sujet en pastilles,
 * cours concerné choisi dans la liste des cours récents (plus rien à
 * recopier), message avec compteur, rappel téléphonique facultatif. Même
 * envoi que ContactSupportForm (SupportRepository.sendContactRequest).
 */
const HelpV2Form = ({ teachr, client, defaultSubject, defaultCallback = false, defaultContext }: Props) => {
    const account: any = teachr ?? client;
    const dispatch = useDispatch();
    const { primary, fontFamily } = useDv2Theme();
    const [subject, setSubject] = useState<string>(defaultSubject && SUPPORT_SUBJECTS.includes(defaultSubject) ? defaultSubject : "");
    const [courseKey, setCourseKey] = useState("");
    const [message, setMessage] = useState("");
    const [callback, setCallback] = useState<boolean>(defaultCallback);
    const [phone, setPhone] = useState("");
    const [slot, setSlot] = useState(CALLBACK_SLOTS[2]);
    const [errors, setErrors] = useState<{ subject?: string; message?: string; phone?: string }>({});
    const [sending, setSending] = useState(false);
    const [courses, setCourses] = useState<TeachrCourseSessionInfo[]>([]);
    const [clientSessions, setClientSessions] = useState<ClientSessionInfo[]>([]);

    // Cours des 30 derniers jours et des 30 prochains, pour désigner le dossier concerné.
    useEffect(() => {
        if (!teachr?.id) return undefined;
        let cancelled = false;
        const from = moment().subtract(30, "days").format("YYYY-MM-DD");
        const to = moment().add(30, "days").format("YYYY-MM-DD");
        PunctualCourseRepository.getTeachrCourseSessions(teachr.id, from, to)
            .then((response) => { if (!cancelled) setCourses(Array.isArray(response?.sessions) ? response.sessions : []); })
            .catch(() => undefined);
        return () => { cancelled = true; };
    }, [teachr?.id]);

    // Client : ses prochaines séances et ses dernières séances passées.
    useEffect(() => {
        if (!client?.id || teachr?.id) return undefined;
        let cancelled = false;
        Promise.allSettled([
            ClientSessionRepository.getUpcoming(client.id, 0, 20),
            ClientSessionRepository.getPast(client.id, 0, 20),
        ]).then(([upcoming, past]) => {
            if (cancelled) return;
            const list = [
                ...(upcoming.status === "fulfilled" ? upcoming.value?.sessions ?? [] : []),
                ...(past.status === "fulfilled" ? past.value?.sessions ?? [] : []),
            ];
            setClientSessions(list);
        });
        return () => { cancelled = true; };
    }, [client?.id, teachr?.id]);

    const courseOptions = useMemo<CourseOption[]>(() => {
        const seen = new Set<string>();
        if (client?.id && !teachr?.id) {
            return [...clientSessions]
                .filter((s) => !!s.date)
                .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())
                .filter((s) => {
                    const key = `${s.course?.id ?? "c"}-${s.date}`;
                    if (seen.has(key)) return false;
                    seen.add(key);
                    return true;
                })
                .map((s) => {
                    const teachrName = s.teachr ? [s.teachr.firstName, s.teachr.lastName].filter(Boolean).map((v) => capitalizeFirstLetter(v)).join(" ") : "";
                    const subject = s.course?.subject ? capitalizeFirstLetter(s.course.subject) : capitalizeFirstLetter(vocab.course());
                    return {
                        key: `${s.course?.id ?? "c"}-${s.date}`,
                        label: `${subject}${teachrName ? ` avec ${teachrName}` : ""}, ${dayLabel(s.date as string).toLowerCase()}, ${clock(s.date as string)}`,
                        context: `${vocab.course()} n°${s.course?.id ?? "?"} du ${moment(s.date).format("DD/MM/YYYY")} à ${clock(s.date as string)}${teachrName ? ` avec ${teachrName}` : ""}`,
                    };
                });
        }
        return [...courses]
            .sort((a, b) => moment(b.date).valueOf() - moment(a.date).valueOf())
            .filter((c) => {
                const key = `${c.courseId ?? "c"}-${c.date}`;
                if (seen.has(key)) return false;
                seen.add(key);
                return true;
            })
            .map((c) => ({
                key: `${c.courseId ?? "c"}-${c.date}`,
                label: `${c.studentName}, ${dayLabel(c.date).toLowerCase()}, ${clock(c.date)}`,
                context: `${vocab.course()} n°${c.courseId ?? "?"} du ${moment(c.date).format("DD/MM/YYYY")} à ${clock(c.date)} avec ${c.studentName}`,
            }));
    }, [courses, clientSessions, client?.id, teachr?.id]);

    const validate = () => {
        const next: typeof errors = {};
        if (!subject) next.subject = "Choisissez le sujet de votre demande.";
        if (message.trim().length < MIN_MESSAGE_LENGTH) next.message = `Décrivez votre demande en quelques mots (${MIN_MESSAGE_LENGTH} caractères minimum).`;
        if (message.length > MAX_MESSAGE_LENGTH) next.message = `Votre message dépasse ${MAX_MESSAGE_LENGTH} caractères.`;
        if (callback && phone.trim().length < 10) next.phone = "Indiquez le numéro auquel vous rappeler.";
        setErrors(next);
        return Object.keys(next).length === 0;
    };

    const send = async () => {
        if (!validate() || sending) return;
        setSending(true);
        const course = courseOptions.find((c) => c.key === courseKey);
        const contextParts = [course?.context ?? defaultContext, callback ? `Rappel souhaité ${slot.toLowerCase()}` : null].filter(Boolean);
        try {
            await SupportRepository.sendContactRequest({
                subject,
                message: message.trim(),
                phone: callback ? phone.trim() : undefined,
                context: contextParts.length ? contextParts.join(". ") : undefined,
            });
            dispatch({ type: SHOW_SUCCESS, payload: "Votre demande a bien été transmise à notre équipe. Nous revenons vers vous rapidement." });
            setMessage("");
            setCourseKey("");
            setCallback(false);
            setPhone("");
            setSubject("");
        } catch {
            dispatch({ type: SHOW_ERROR, payload: "Votre demande n'a pas pu être envoyée. Réessayez dans quelques instants ou écrivez-nous directement par e-mail." });
        } finally {
            setSending(false);
        }
    };

    return (
        <Card style={{ display: "flex", flexDirection: "column", gap: 22, padding: 28, fontFamily }} as="section" aria-label="Nous écrire">
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <div style={{ fontWeight: 800, fontSize: 15 }} id="dv2-help-subject-label">Votre demande concerne</div>
                <div role="radiogroup" aria-labelledby="dv2-help-subject-label" style={{ display: "flex", flexWrap: "wrap", gap: 8 }}>
                    {SUPPORT_SUBJECTS.map((s) => {
                        const active = subject === s;
                        return (
                            <button
                                key={s}
                                type="button"
                                role="radio"
                                aria-checked={active}
                                onClick={() => { setSubject(s); setErrors((e) => ({ ...e, subject: undefined })); }}
                                style={{ padding: "9px 14px", borderRadius: 999, fontSize: 14, fontWeight: 700, fontFamily, cursor: "pointer", background: active ? primary : "#fff", color: active ? "#fff" : DV2.text, border: active ? "1px solid transparent" : `1px solid #C9D3E0` }}
                            >
                                {s}
                            </button>
                        );
                    })}
                </div>
                {errors.subject && <FieldError>{errors.subject}</FieldError>}
            </div>

            <div>
                <FieldLabel htmlFor="dv2-help-course">{vocab.theCourse().charAt(0).toUpperCase() + vocab.theCourse().slice(1)} concerné, si besoin</FieldLabel>
                <select id="dv2-help-course" value={courseKey} onChange={(e) => setCourseKey(e.target.value)} style={{ ...fieldStyle(), fontFamily, fontWeight: 500, appearance: "auto" as any }}>
                    <option value="">Aucun {vocab.course()} en particulier</option>
                    {courseOptions.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
                </select>
                <Hint>Nous retrouvons le dossier plus vite, vous n'avez rien à recopier.</Hint>
            </div>

            <div>
                <FieldLabel htmlFor="dv2-help-message">Votre message</FieldLabel>
                <textarea
                    id="dv2-help-message"
                    value={message}
                    onChange={(e) => { setMessage(e.target.value); setErrors((er) => ({ ...er, message: undefined })); }}
                    rows={6}
                    placeholder="Expliquez-nous votre demande : de quoi il s'agit, depuis quand, ce que vous avez déjà essayé."
                    aria-invalid={!!errors.message}
                    aria-describedby="dv2-help-message-hint"
                    style={{ ...fieldStyle(!!errors.message), fontFamily, fontWeight: 500, fontSize: 15, lineHeight: 1.5, resize: "vertical", minHeight: 150 }}
                />
                <div id="dv2-help-message-hint" style={{ display: "flex", justifyContent: "space-between", gap: 8 }}>
                    {errors.message ? <FieldError>{errors.message}</FieldError> : <Hint>Plus vous êtes précis, plus la réponse est rapide.</Hint>}
                    <span style={{ fontSize: 12, color: message.length > MAX_MESSAGE_LENGTH ? DV2.danger : DV2.placeholder, whiteSpace: "nowrap", marginTop: 6 }}>{message.length} caractère{message.length > 1 ? "s" : ""}</span>
                </div>
            </div>

            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                <label style={{ display: "flex", alignItems: "center", gap: 10, cursor: "pointer", fontSize: 15, fontWeight: 700 }}>
                    <input type="checkbox" checked={callback} onChange={(e) => setCallback(e.target.checked)} style={{ width: 22, height: 22, accentColor: primary }} />
                    Je préfère être rappelé
                </label>
                {callback && (
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:pl-8">
                        <div>
                            <FieldLabel htmlFor="dv2-help-phone">Téléphone</FieldLabel>
                            <input id="dv2-help-phone" type="tel" autoComplete="tel" value={phone} onChange={(e) => { setPhone(e.target.value); setErrors((er) => ({ ...er, phone: undefined })); }} placeholder="06 12 34 56 78" aria-invalid={!!errors.phone} style={{ ...fieldStyle(!!errors.phone), fontFamily }} />
                            {errors.phone && <FieldError>{errors.phone}</FieldError>}
                        </div>
                        <div>
                            <FieldLabel htmlFor="dv2-help-slot">Plutôt</FieldLabel>
                            <select id="dv2-help-slot" value={slot} onChange={(e) => setSlot(e.target.value)} style={{ ...fieldStyle(), fontFamily, appearance: "auto" as any }}>
                                {CALLBACK_SLOTS.map((s) => <option key={s} value={s}>{s}</option>)}
                            </select>
                        </div>
                    </div>
                )}
            </div>

            <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3" style={{ paddingTop: 6, borderTop: `1px solid ${DV2.borderSoft}` }}>
                <div style={{ fontSize: 13, color: DV2.muted }}>Envoyé avec votre nom et votre e-mail{account?.mail ? `, ${account.mail}` : ""}.</div>
                <Button label="Envoyer ma demande" onClick={send} loading={sending} disabled={sending} />
            </div>
        </Card>
    );
};

export default HelpV2Form;
